import { GameEngine, GameEvent, Seat, clone, err } from "../engine.js";
import { RngState, shuffle } from "../rng.js";

/**
 * Dominoes (double-six) — Samra rules v1 (docs/games/domino.md)
 * - 2–4 players, 7 tiles each. With 4 players, partners sit opposite (teams 0+2, 1+3).
 * - With fewer than 4 players the rest form a boneyard: draw until you can play.
 * - Round ends when a player empties their hand, or the game is blocked.
 * - Round winner (team) scores the pips left in all opponents' hands.
 *   Blocked game: the side with the lowest pip count wins the round.
 * - First to reach the target score (default 100) wins the match.
 */

export type Tile = [number, number];

export interface DominoOptions {
  target: number;
  teams: boolean; // only applies to 4 players
}

export interface DominoState {
  n: number;
  hands: Tile[][];
  boneyard: Tile[];
  chain: Tile[]; // oriented: chain[i][1] === chain[i+1][0]
  turn: Seat;
  passes: number;
  scores: number[]; // per side (team or seat)
  round: number;
  roundStarter: Seat;
  over: boolean;
  options: DominoOptions;
  rng: RngState;
  lastRound: { winnerSide: number; points: number; blocked: boolean } | null;
}

export type DominoMove =
  | { type: "play"; tile: Tile; side: "left" | "right" }
  | { type: "draw" }
  | { type: "pass" };

export interface DominoView {
  n: number;
  hand: Tile[];
  handCounts: number[];
  boneyardCount: number;
  chain: Tile[];
  turn: Seat;
  scores: number[];
  round: number;
  over: boolean;
  options: DominoOptions;
  teams: boolean;
  lastRound: DominoState["lastRound"];
  canDraw: boolean;
  canPass: boolean;
  playable: { tile: Tile; sides: ("left" | "right")[] }[];
  /** Revealed only when a round has ended. */
  revealedHands?: Tile[][];
}

const pips = (t: Tile) => t[0] + t[1];
const sameTile = (a: Tile, b: Tile) =>
  (a[0] === b[0] && a[1] === b[1]) || (a[0] === b[1] && a[1] === b[0]);

function fullSet(): Tile[] {
  const out: Tile[] = [];
  for (let a = 0; a <= 6; a++) for (let b = a; b <= 6; b++) out.push([a, b]);
  return out;
}

function useTeams(s: { n: number; options: DominoOptions }) {
  return s.n === 4 && s.options.teams;
}
function sideOf(s: DominoState, seat: Seat) {
  return useTeams(s) ? seat % 2 : seat;
}
function sideCount(s: DominoState) {
  return useTeams(s) ? 2 : s.n;
}

function ends(s: DominoState): [number, number] | null {
  if (!s.chain.length) return null;
  return [s.chain[0][0], s.chain[s.chain.length - 1][1]];
}

function playableFor(s: DominoState, seat: Seat) {
  const e = ends(s);
  return s.hands[seat]
    .map((tile) => {
      if (!e) return { tile, sides: ["right"] as ("left" | "right")[] };
      const sides: ("left" | "right")[] = [];
      if (tile[0] === e[0] || tile[1] === e[0]) sides.push("left");
      if (tile[0] === e[1] || tile[1] === e[1]) sides.push("right");
      return { tile, sides };
    })
    .filter((p) => p.sides.length > 0);
}

function deal(s: DominoState) {
  const tiles = shuffle(s.rng, fullSet());
  s.hands = Array.from({ length: s.n }, (_, i) => tiles.slice(i * 7, i * 7 + 7));
  s.boneyard = tiles.slice(s.n * 7);
  s.chain = [];
  s.passes = 0;
}

function highestDoubleHolder(s: DominoState): Seat {
  for (let v = 6; v >= 0; v--) {
    for (let p = 0; p < s.n; p++) {
      if (s.hands[p].some((t) => t[0] === v && t[1] === v)) return p;
    }
  }
  let best = 0;
  let bestPips = -1;
  s.hands.forEach((h, p) =>
    h.forEach((t) => {
      if (pips(t) > bestPips) {
        bestPips = pips(t);
        best = p;
      }
    }),
  );
  return best;
}

function endRound(s: DominoState, winnerSeat: Seat | null, events: GameEvent[]) {
  const sides = sideCount(s);
  const sidePips = Array(sides).fill(0);
  s.hands.forEach((h, p) => (sidePips[sideOf(s, p)] += h.reduce((a, t) => a + pips(t), 0)));
  let winnerSide: number;
  let blocked = false;
  if (winnerSeat != null) {
    winnerSide = sideOf(s, winnerSeat);
  } else {
    blocked = true;
    const min = Math.min(...sidePips);
    const lows = sidePips.map((v, i) => (v === min ? i : -1)).filter((i) => i >= 0);
    winnerSide = lows.length === 1 ? lows[0] : -1; // tie: nobody scores
  }
  const points =
    winnerSide < 0 ? 0 : sidePips.reduce((a, v, i) => (i === winnerSide ? a : a + v), 0);
  if (winnerSide >= 0) s.scores[winnerSide] += points;
  s.lastRound = { winnerSide, points, blocked };
  events.push({ type: "round_end", winnerSide, points, blocked, hands: clone(s.hands) });
  if (s.scores.some((v) => v >= s.options.target)) {
    s.over = true;
    events.push({ type: "match_end", scores: s.scores.slice() });
    return;
  }
  s.round++;
  const starter =
    winnerSeat != null ? winnerSeat : (s.roundStarter + 1) % s.n;
  deal(s);
  s.roundStarter = starter;
  s.turn = starter;
  events.push({ type: "round_start", round: s.round, starter });
}

export const domino: GameEngine<DominoState, DominoMove, DominoView, DominoOptions> = {
  id: "domino",
  minPlayers: 2,
  maxPlayers: 4,
  playerCounts: [2, 3, 4],
  defaultOptions: { target: 100, teams: true },

  init(n, seed, options) {
    if (n < 2 || n > 4) throw new Error("domino supports 2-4 players");
    const s: DominoState = {
      n,
      hands: [],
      boneyard: [],
      chain: [],
      turn: 0,
      passes: 0,
      scores: [],
      round: 1,
      roundStarter: 0,
      over: false,
      options: { ...this.defaultOptions, ...options },
      rng: { s: seed | 0 },
      lastRound: null,
    };
    s.scores = Array(sideCount(s)).fill(0);
    deal(s);
    s.roundStarter = s.turn = highestDoubleHolder(s);
    return s;
  },

  currentSeat: (s) => (s.over ? null : s.turn),

  applyMove(state, seat, move) {
    if (state.over) return err("game_over");
    if (seat !== state.turn) return err("not_your_turn");
    const s = clone(state);
    const events: GameEvent[] = [];
    const playable = playableFor(s, seat);

    if (move.type === "play") {
      if (!Array.isArray(move.tile) || move.tile.length !== 2) return err("bad_tile");
      const idx = s.hands[seat].findIndex((t) => sameTile(t, move.tile));
      if (idx < 0) return err("tile_not_in_hand");
      const tile = s.hands[seat][idx];
      const e = ends(s);
      let placed: Tile;
      if (!e) {
        placed = [tile[0], tile[1]];
        s.chain.push(placed);
      } else if (move.side === "left") {
        if (tile[1] === e[0]) placed = [tile[0], tile[1]];
        else if (tile[0] === e[0]) placed = [tile[1], tile[0]];
        else return err("does_not_match");
        s.chain.unshift(placed);
      } else if (move.side === "right") {
        if (tile[0] === e[1]) placed = [tile[0], tile[1]];
        else if (tile[1] === e[1]) placed = [tile[1], tile[0]];
        else return err("does_not_match");
        s.chain.push(placed);
      } else return err("bad_side");
      s.hands[seat].splice(idx, 1);
      s.passes = 0;
      events.push({ type: "played", seat, tile: placed, side: move.side });
      if (s.hands[seat].length === 0) {
        events.push({ type: "domino", seat });
        endRound(s, seat, events);
      } else {
        s.turn = (seat + 1) % s.n;
      }
      return { ok: true, state: s, events };
    }

    if (move.type === "draw") {
      if (playable.length) return err("must_play");
      if (!s.boneyard.length) return err("boneyard_empty");
      const t = s.boneyard.pop()!;
      s.hands[seat].push(t);
      events.push({ type: "drew", seat });
      return { ok: true, state: s, events };
    }

    if (move.type === "pass") {
      if (playable.length) return err("must_play");
      if (s.boneyard.length) return err("must_draw");
      s.passes++;
      events.push({ type: "passed", seat });
      if (s.passes >= s.n) {
        events.push({ type: "blocked" });
        endRound(s, null, events);
      } else {
        s.turn = (seat + 1) % s.n;
      }
      return { ok: true, state: s, events };
    }
    return err("unknown_move");
  },

  view(s, seat) {
    const mine = seat >= 0 && seat < s.n;
    const playable = mine && seat === s.turn && !s.over ? playableFor(s, seat) : [];
    return {
      n: s.n,
      hand: mine ? clone(s.hands[seat]) : [],
      handCounts: s.hands.map((h) => h.length),
      boneyardCount: s.boneyard.length,
      chain: clone(s.chain),
      turn: s.turn,
      scores: s.scores.slice(),
      round: s.round,
      over: s.over,
      options: s.options,
      teams: useTeams(s),
      lastRound: s.lastRound,
      canDraw: mine && seat === s.turn && !playable.length && s.boneyard.length > 0,
      canPass: mine && seat === s.turn && !playable.length && s.boneyard.length === 0,
      playable,
      revealedHands: s.over ? clone(s.hands) : undefined,
    };
  },

  result(s) {
    if (!s.over) return null;
    const sides = sideCount(s);
    const order = Array.from({ length: sides }, (_, i) => i).sort(
      (a, b) => s.scores[b] - s.scores[a],
    );
    const seatsOf = (side: number) =>
      useTeams(s) ? [side, side + 2] : [side];
    return {
      ranking: order.map(seatsOf),
      scores: Array.from({ length: s.n }, (_, p) => s.scores[sideOf(s, p)]),
      reason: "target_reached",
    };
  },

  autoMove(s, seat) {
    if (s.over || s.turn !== seat) return null;
    const p = playableFor(s, seat);
    if (p.length) {
      const best = p.reduce((a, b) => (pips(b.tile) > pips(a.tile) ? b : a));
      return { type: "play", tile: best.tile, side: best.sides[0] };
    }
    return s.boneyard.length ? { type: "draw" } : { type: "pass" };
  },
};
